/**
 * Scripted scenario comms. Maritime Operations Center (MOC) ↔ defensive USVs.
 * Messages are sparse: only during anticipation, detection, convergence, and recovery —
 * most of the timeline has no circuit traffic (`step` 0).
 *
 * Each entry uses normalized story time `u` in [0,1] over the loaded vessel range (`u0` ≤ u < `u1`,
 * last entry closed at u = 1).
 */

import { EXERCISE_VESSEL_ROLES } from './realisticExerciseScenarioData';

export const MOC_SHIP_LON_LAT = [-71.3178, 41.5258];

function exerciseUuidByLabel(match) {
  const e = Object.entries(EXERCISE_VESSEL_ROLES).find(([, v]) => match(v.label));
  return e ? e[0] : null;
}

/** c375714f… is AUV orange; script “Blue-2” comms anchor to Defense USV-3 track (539acd93…). */
const DEFENSE_USV_2_COMMS_UUID = '539acd93-0c4a-4584-b7ca-0bcd017f93d5';
const AUV_SUBSURFACE_COMMS_UUID = 'c375714f-a390-4cb7-96c5-afb8e187790d';

export const SCENARIO_PARTY_POSITION_SOURCE = {
  'Maritime Operations Center': { kind: 'fixed', lonLat: MOC_SHIP_LON_LAT },
  'Defense USV-1': { kind: 'vessel', uuid: exerciseUuidByLabel((l) => l === 'Defense USV-1') },
  'Defense USV-2': { kind: 'vessel', uuid: DEFENSE_USV_2_COMMS_UUID },
  'Threat track (red)': { kind: 'vessel', uuid: exerciseUuidByLabel((l) => l.includes('Threat USV')) },
  'AUV (subsurface)': { kind: 'vessel', uuid: AUV_SUBSURFACE_COMMS_UUID }
};

export const SCENARIO_COMMS_CHANNEL_STYLES = {
  'moc-command': {
    label: 'MOC ↔ surface',
    bar: 'border-l-[#817c9e] bg-violet-50 dark:bg-violet-950/40 dark:border-l-[#a49bc4]',
    bubble: 'border-[#817c9e]/75 bg-violet-50/95 dark:bg-violet-950/90 dark:border-[#a49bc4]',
    embedded: {
      border: 'border-l-violet-500',
      badge: 'bg-violet-100 text-violet-900',
      route: 'text-violet-700'
    }
  },
  'moc-subsea': {
    label: 'MOC ↔ subsea',
    bar: 'border-l-cyan-600 bg-cyan-50 dark:bg-cyan-950/40 dark:border-l-cyan-400',
    bubble: 'border-cyan-400/80 bg-cyan-50/95 dark:bg-cyan-950/90 dark:border-cyan-500',
    embedded: {
      border: 'border-l-cyan-600',
      badge: 'bg-cyan-100 text-cyan-900',
      route: 'text-cyan-800'
    }
  },
  'moc-threat': {
    label: 'MOC ↔ threat / control',
    bar: 'border-l-orange-600 bg-orange-50 dark:bg-orange-950/40 dark:border-l-orange-400',
    bubble: 'border-orange-400/80 bg-orange-50/95 dark:bg-orange-950/90 dark:border-orange-600',
    embedded: {
      border: 'border-l-orange-500',
      badge: 'bg-orange-100 text-orange-900',
      route: 'text-orange-800'
    }
  },
  'moc-recovery': {
    label: 'MOC — recovery',
    bar: 'border-l-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:border-l-emerald-400',
    bubble: 'border-emerald-400/80 bg-emerald-50/95 dark:bg-emerald-950/90 dark:border-emerald-600',
    embedded: {
      border: 'border-l-emerald-600',
      badge: 'bg-emerald-100 text-emerald-900',
      route: 'text-emerald-800'
    }
  }
};

/**
 * Threat-focused beats only. Gaps in u leave step 0 (no map comms / no new traffic).
 * Windows are fractions of story index (frame / last frame), so they track the scrubber, not wall clock.
 * Recovery tail ~8% of frames; long quiet run after.
 *
 * Client-facing full transcript (static .txt, same wording; update when this array changes):
 * public/exercise-comms-transcript.txt
 */
export const SCENARIO_COMMS_SCRIPT = [
  {
    u0: 0.06,
    u1: 0.1,
    channel: 'moc-command',
    narrativePhase: 'Anticipation',
    from: 'Maritime Operations Center',
    to: 'Defense USV-1',
    text: 'MOC to Blue-1: elevated posture — picture shows a fast-close surface contact may enter the Op Alpha box. Shift EO/IR to long-range sweep; report any bow-on traffic.',
    mapAnchor: [-71.3165, 41.53435]
  },
  {
    u0: 0.1,
    u1: 0.14,
    channel: 'moc-command',
    narrativePhase: 'Anticipation',
    from: 'Maritime Operations Center',
    to: 'Defense USV-2',
    text: 'MOC to Blue-2: tighten southern arc; keep threat axis on your starboard bow. Do not close inside the polygon without release.',
    mapAnchor: [-71.3152, 41.535]
  },
  {
    u0: 0.14,
    u1: 0.2,
    channel: 'moc-threat',
    narrativePhase: 'Detection',
    from: 'Defense USV-1',
    to: 'Maritime Operations Center',
    text: 'Blue-1 to MOC: faint high-speed surface return, correlating with exercise red track. Request classify-and-illuminate authority.',
    mapAnchor: [-71.3165, 41.53435]
  },
  {
    u0: 0.2,
    u1: 0.26,
    channel: 'moc-threat',
    narrativePhase: 'Detection',
    from: 'Maritime Operations Center',
    to: 'Defense USV-1',
    text: 'MOC to Blue-1: unknown contact — classify intent. You are cleared to illuminate and track. Weapons tight; confirm ROE verbally.',
    mapAnchor: [-71.3165, 41.53435]
  },
  {
    u0: 0.26,
    u1: 0.32,
    channel: 'moc-threat',
    narrativePhase: 'Convergence',
    from: 'Maritime Operations Center',
    to: 'Defense USV-2',
    text: 'MOC to Blue-2: blocking sector now — support Blue-1 with optics. Pass bearing, range, and speed every thirty seconds.',
    mapAnchor: [-71.3152, 41.535]
  },
  {
    u0: 0.32,
    u1: 0.4,
    channel: 'moc-threat',
    narrativePhase: 'Convergence',
    from: 'Defense USV-1',
    to: 'Maritime Operations Center',
    text: 'Blue-1 to MOC: in visual contact with red track; holding stand-off per ROE. Convergence geometry stable — no aggressive closure.',
    mapAnchor: [-71.3165, 41.53435]
  },
  {
    u0: 0.4,
    u1: 0.5,
    channel: 'moc-recovery',
    narrativePhase: 'Recovery',
    from: 'Maritime Operations Center',
    to: 'Defense USV-1',
    text: 'MOC to Blue-1: exercise control — red track is contained. Cease illumination; hold station for BDA pass-down. Stand by for recovery tasking.',
    mapAnchor: [-71.3165, 41.53435]
  },
  {
    u0: 0.5,
    u1: 0.58,
    channel: 'moc-recovery',
    narrativePhase: 'Recovery',
    from: 'Maritime Operations Center',
    to: 'Defense USV-2',
    text: 'MOC to Blue-2: secure sector; log BDA and return to loiter. No further incidents — circuit silent unless called.',
    mapAnchor: [-71.3152, 41.535]
  }
];

/**
 * @param {number} u — normalized time in [0,1] over vessel story range
 * @returns {number} 1-based step index into SCENARIO_COMMS_SCRIPT, or 0 when no message applies
 */
export function scenarioCommsStepFromNormalizedTime(u) {
  const x = Math.max(0, Math.min(1, u));
  const n = SCENARIO_COMMS_SCRIPT.length;
  for (let i = 0; i < n; i++) {
    const { u0, u1 } = SCENARIO_COMMS_SCRIPT[i];
    const last = i === n - 1;
    if (last) {
      if (x >= u0 && x <= u1) return i + 1;
    } else if (x >= u0 && x < u1) {
      return i + 1;
    }
  }
  return 0;
}

/**
 * Highest 0-based script index whose beat is fully past at normalized time `u`
 * (matches window boundaries used by {@link scenarioCommsStepFromNormalizedTime}).
 * Returns -1 when no beat has finished yet.
 */
export function scenarioCommsMaxFullyPassedMessageIndex(u) {
  const x = Math.max(0, Math.min(1, u));
  const n = SCENARIO_COMMS_SCRIPT.length;
  let max = -1;
  for (let j = 0; j < n; j++) {
    const { u1 } = SCENARIO_COMMS_SCRIPT[j];
    const last = j === n - 1;
    if (last) {
      if (x > u1 || x >= 1) max = j;
    } else if (x >= u1) {
      max = j;
    }
  }
  return max;
}
